/* ============================================================================
   ELUNORUS — Product detail page behaviour

   Plain DOM code, no framework. Alpine is loaded globally by layout/theme.liquid
   for the header and cart drawer, but the PDP deliberately does not wait on it:
   this is the landing page for paid traffic and it should be interactive as
   soon as the HTML is parsed.

   Accordions are native <details>, so none of their behaviour lives here.
   ========================================================================== */
(function () {
  'use strict';

  function all(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /* ── A+ show more ───────────────────────────────────────────────────────
     Wired before the main-product guard below, because the A+ sheet is its own
     section and should still expand on any template that carries it. */
  all('[data-epdp-aplus-toggle]').forEach(function (btn) {
    var clip = document.querySelector('[data-epdp-aplus-clip]');
    if (!clip) return;
    var label = btn.querySelector('[data-epdp-aplus-toggle-label]');
    btn.addEventListener('click', function () {
      var open = clip.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (label) label.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      // Collapsing from far down the sheet would leave the viewport below the
      // section entirely, so bring the top of it back into view.
      if (!open) {
        var top = clip.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    });
  });

  var root = document.querySelector('[data-epdp]');
  if (!root) return;

  var dataEl = document.getElementById(root.dataset.epdp);
  if (!dataEl) return;

  var data = JSON.parse(dataEl.textContent);
  var variants = data.variants || [];
  var moneyFormat = data.moneyFormat || '';

  /* ── Money ──────────────────────────────────────────────────────────────
     Formatted through the shop's own money_format so the figure always matches
     what Liquid rendered server-side. */
  function money(cents) {
    var parts = (cents / 100).toFixed(2).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (!moneyFormat) return '$' + whole + '.' + parts[1];
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, whole + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
  }

  /* ── Gallery ────────────────────────────────────────────────────────────
     Everything below runs inside gallery(), called in a try/catch. This file
     is one IIFE, so anything that throws here used to take the variant picker,
     the quantity stepper and add-to-cart down with it — the whole page would
     go dead from a single gallery bug. It is contained now, and the failure
     is logged rather than silent. */
  // Set by gallery() once it has built showImage. The variant picker lives
  // outside that closure but needs to swap to a variant's photo, so the
  // function is handed out here rather than reached into.
  //
  // It stays null if gallery() threw before getting this far, and the variant
  // picker checks for that — which is the whole point of the isolation: a
  // broken gallery must not stop anyone choosing a size.
  var swapToImage = null;

  function gallery() {
  var mainImg = root.querySelector('[data-epdp-main-image]');
  var thumbs = all('[data-epdp-thumb]', root);

  var thumbRail = root.querySelector('.epdp-thumbs');
  var prevBtn = root.querySelector('[data-epdp-prev]');
  var nextBtn = root.querySelector('[data-epdp-next]');
  var currentIndex = 0;

  // The fades are painted on the wrapper, not the rail: a pseudo-element on a
  // scroll container scrolls away with the content it is meant to cover.
  var railWrap = thumbRail ? (thumbRail.closest('.epdp-thumbs-wrap') || thumbRail) : null;

  // Fail-safe class logic, deliberately inverted. The right-hand fade is ON in
  // CSS by default and this only ever turns it OFF at the end of the rail, so
  // if any of this never runs the rail still reads as scrollable rather than
  // looking like a cut-off mistake.
  function syncRailEdges() {
    if (!thumbRail || !railWrap) return;
    var max = thumbRail.scrollWidth - thumbRail.clientWidth;
    railWrap.classList.toggle('has-prev', thumbRail.scrollLeft > 4);
    railWrap.classList.toggle('at-end', max <= 4 || thumbRail.scrollLeft >= max - 4);
  }

  // The rail is a horizontal scroller under the photo on a phone and a vertical
  // one beside it on desktop, so every measurement has to pick its axis first.
  // Centring used to assume horizontal, which meant that on desktop — where the
  // rail is the only one long enough to need it — it never scrolled at all.
  function railAxis() {
    if (!thumbRail) return null;
    if (thumbRail.scrollHeight - thumbRail.clientHeight > 4) return 'y';
    if (thumbRail.scrollWidth - thumbRail.clientWidth > 4) return 'x';
    return null;
  }

  // scrollTo(options) is not honoured everywhere: older WebKit reads the object
  // as scrollTo(x, y), gets NaN and jumps the rail to 0. Assigning scrollTop /
  // scrollLeft works in every engine, so that is the fallback.
  function railScrollTo(axis, pos) {
    var vertical = axis === 'y';
    var max = vertical
      ? thumbRail.scrollHeight - thumbRail.clientHeight
      : thumbRail.scrollWidth - thumbRail.clientWidth;
    var target = Math.max(0, Math.min(pos, Math.max(0, max)));
    var read = function () { return vertical ? thumbRail.scrollTop : thumbRail.scrollLeft; };
    var write = function (v) { if (vertical) thumbRail.scrollTop = v; else thumbRail.scrollLeft = v; };
    var was = read();

    try {
      thumbRail.scrollTo(vertical
        ? { top: target, behavior: 'smooth' }
        : { left: target, behavior: 'smooth' });
    } catch (e) {
      write(target);
    }
    // scrollTo can silently no-op; if nothing moved at all, force it.
    setTimeout(function () {
      if (read() === was && Math.abs(read() - target) > 2) write(target);
      syncRailEdges();
    }, 400);
  }

  // Scroll the rail so the active thumbnail sits in the middle.
  //
  // Measured from bounding rects, NOT offsetTop/offsetLeft: .epdp-thumbs is not
  // a positioned element, so those are relative to whatever ancestor happens to
  // be positioned — which is not the rail, and the maths lands nowhere near the
  // centre.
  function centerThumb(thumb) {
    if (!thumbRail || !thumb) return;
    var axis = railAxis();
    if (!axis) return;

    var railRect = thumbRail.getBoundingClientRect();
    var thumbRect = thumb.getBoundingClientRect();

    if (axis === 'y') {
      if (!railRect.height || !thumbRect.height) return;
      var dy = (thumbRect.top - railRect.top) - (railRect.height - thumbRect.height) / 2;
      railScrollTo('y', thumbRail.scrollTop + dy);
    } else {
      if (!railRect.width || !thumbRect.width) return;
      var dx = (thumbRect.left - railRect.left) - (railRect.width - thumbRect.width) / 2;
      railScrollTo('x', thumbRail.scrollLeft + dx);
    }
  }

  function syncArrows() {
    if (prevBtn) prevBtn.disabled = currentIndex <= 0;
    if (nextBtn) nextBtn.disabled = currentIndex >= thumbs.length - 1;
  }

  function showImage(index) {
    var thumb = thumbs[index];
    if (!thumb || !mainImg) return;
    var img = thumb.querySelector('img');
    var stage = mainImg.parentElement;
    var next = thumb.dataset.full || (img && img.src);

    currentIndex = index;
    thumbs.forEach(function (t) { t.setAttribute('aria-current', t === thumb ? 'true' : 'false'); });
    syncArrows();

    centerThumb(thumb);

    if (!next || next === mainImg.getAttribute('src')) return;

    // Fade out, swap, fade back in once the new file has decoded — otherwise
    // the browser paints a partly-loaded image mid-transition. The timeout is
    // the safety net for a load event that never arrives (cache, error).
    var settle = function () {
      if (stage) stage.classList.remove('is-swapping');
      mainImg.removeEventListener('load', settle);
      mainImg.removeEventListener('error', settle);
    };
    if (stage) stage.classList.add('is-swapping');
    mainImg.addEventListener('load', settle);
    mainImg.addEventListener('error', settle);
    setTimeout(settle, 600);

    // srcset before src: with a srcset present the browser picks the source
    // from it, so setting src first only to replace the srcset afterwards
    // leaves a window where the old candidate list is still in force.
    if (thumb.dataset.fullSrcset) mainImg.srcset = thumb.dataset.fullSrcset;
    else mainImg.removeAttribute('srcset');
    mainImg.src = next;
    if (img) mainImg.alt = img.alt || '';
  }

  swapToImage = showImage;

  thumbs.forEach(function (thumb, i) {
    thumb.addEventListener('click', function () { showImage(i); });
  });

  if (prevBtn) prevBtn.addEventListener('click', function () { showImage(Math.max(0, currentIndex - 1)); });
  if (nextBtn) nextBtn.addEventListener('click', function () { showImage(Math.min(thumbs.length - 1, currentIndex + 1)); });
  if (thumbRail) {
    thumbRail.addEventListener('scroll', syncRailEdges, { passive: true });
    window.addEventListener('resize', syncRailEdges);
    syncRailEdges();
    // The rail is measured before its thumbnails have decoded, so the first
    // reading can be taken against a collapsed scrollWidth. Re-measure whenever
    // the box actually changes size, after each thumbnail lands, and once more
    // when the page has finished loading.
    if (window.ResizeObserver) new ResizeObserver(syncRailEdges).observe(thumbRail);
    all('img', thumbRail).forEach(function (im) {
      if (!im.complete) im.addEventListener('load', syncRailEdges, { once: true });
    });
    requestAnimationFrame(syncRailEdges);
    window.addEventListener('load', syncRailEdges);
  }
  syncArrows();

  /* Arrow-key access for the gallery. */
  var galleryEl = root.querySelector('[data-epdp-gallery]');
  if (galleryEl) {
    galleryEl.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var current = thumbs.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
      if (current < 0) current = 0;
      var next = e.key === 'ArrowRight' ? Math.min(thumbs.length - 1, current + 1) : Math.max(0, current - 1);
      if (next === current) return;
      e.preventDefault();
      showImage(next);
      thumbs[next].focus();
    });
  }
  }
  try { gallery(); } catch (e) { console.error('[epdp] gallery failed:', e); }

  /* ── Variants ─────────────────────────────────────────────────────────── */
  var selected = [];
  var current = null;

  var idInput = root.querySelector('[data-epdp-variant-id]');
  var priceEl = root.querySelector('[data-epdp-price]');
  var compareEl = root.querySelector('[data-epdp-compare]');
  var discountEl = root.querySelector('[data-epdp-discount]');
  var labelEls = all('[data-epdp-selected-value]', root);
  var optionBtns = all('[data-epdp-option-value]', root);
  var stickyPrice = document.querySelector('[data-epdp-sticky-price]');
  var atcButtons = all('[data-epdp-atc]');

  function findVariant() {
    return variants.find(function (v) {
      return v.options.every(function (o, i) { return o === selected[i]; });
    }) || null;
  }

  // A value is offerable if some in-stock variant carries it while every other
  // option stays on what the shopper already chose.
  function valueAvailable(index, value) {
    return variants.some(function (v) {
      return v.available && v.options[index] === value && v.options.every(function (o, i) {
        return i === index || o === selected[i];
      });
    });
  }

  function render() {
    var available = !!(current && current.available);
    if (idInput && current) idInput.value = current.id;

    if (priceEl && current) priceEl.textContent = money(current.price);
    if (stickyPrice && current) stickyPrice.textContent = money(current.price);

    var onSale = !!(current && current.compare_at_price > current.price);
    if (priceEl) priceEl.classList.toggle('epdp-price--sale', onSale);
    if (compareEl) {
      compareEl.hidden = !onSale;
      if (onSale) compareEl.textContent = money(current.compare_at_price);
    }
    if (discountEl) {
      discountEl.hidden = !onSale;
      if (onSale) {
        var off = Math.round((current.compare_at_price - current.price) / current.compare_at_price * 100);
        // Prefix comes from the block setting, so changing it in the editor is
        // not undone the moment the shopper picks another size.
        var prefix = discountEl.dataset.savePrefix || 'SAVE';
        discountEl.textContent = prefix + ' ' + off + '%';
      }
    }

    labelEls.forEach(function (el) {
      var i = parseInt(el.dataset.epdpSelectedValue, 10) || 0;
      el.textContent = selected[i] || '';
    });

    optionBtns.forEach(function (btn) {
      var i = parseInt(btn.dataset.epdpOptionIndex, 10);
      var value = btn.dataset.epdpOptionValue;
      btn.setAttribute('aria-pressed', selected[i] === value ? 'true' : 'false');
      btn.disabled = !valueAvailable(i, value);
    });

    // The in-form button carries the price; the docked mobile bar has no room
    // for it and is flagged without data-epdp-show-price.
    atcButtons.forEach(function (btn) {
      btn.disabled = !available;
      var label = btn.querySelector('[data-epdp-atc-label]');
      if (!label) return;
      var base = (btn.dataset.epdpLabel || 'Add to bag').toUpperCase();
      if (!available) { label.textContent = 'SOLD OUT'; return; }
      label.textContent = btn.hasAttribute('data-epdp-show-price')
        ? base + ' · ' + money(current.price)
        : base;
    });

    // Keeps ?variant= honest so a reload or a shared link lands on the same one.
    if (current && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', current.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  optionBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var i = parseInt(btn.dataset.epdpOptionIndex, 10);
      selected[i] = btn.dataset.epdpOptionValue;
      var match = findVariant();
      if (match) {
        current = match;
        if (swapToImage && match.media_index !== null && match.media_index >= 0) {
          swapToImage(match.media_index);
        }
      }
      render();
    });
  });

  var initial = variants.find(function (v) { return v.id === data.initialId; })
             || variants.find(function (v) { return v.available; })
             || variants[0];
  if (initial) {
    selected = initial.options.slice();
    current = initial;
    render();
  }

  /* ── Sticky buy bar ─────────────────────────────────────────────────────
     The bar is revealed once the real Add to bag button has left the viewport,
     on both desktop and mobile. The size chips inside it need no wiring: they
     carry data-epdp-option-value, so the variant code above already owns them.

     The same state is mirrored onto <body> as .epdp-bar-up, because the chat
     launcher and the newsletter teaser are app widgets appended to <body> —
     nothing inside this section can reach them with a CSS selector, and they
     otherwise sit on top of the bar. */
  try {
    var bar = document.querySelector('[data-epdp-stickybar]');
    var anchor = atcButtons.filter(function (b) {
      return !b.closest('[data-epdp-stickybar]');
    })[0];
    if (bar && anchor && window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        var up = !entries[0].isIntersecting;
        bar.classList.toggle('is-visible', up);
        document.body.classList.toggle('epdp-bar-up', up);
      }, { threshold: 0 }).observe(anchor);
    } else if (bar) {
      // No observer, or no in-form button to watch: showing it beats a bar
      // that can never appear.
      bar.classList.add('is-visible');
      document.body.classList.add('epdp-bar-up');
    }
  } catch (e) { console.error('[epdp] sticky bar failed:', e); }

  /* ── Newsletter teaser vs the buy bar ───────────────────────────────────
     The chat launcher moves with a CSS rule. The Shopify Forms teaser will not,
     and the reason is worth writing down: the app renders into a shadow root on
     <shopify-forms-embed> ({ mode: 'open' }, confirmed in its loader), and page
     CSS does not cross a shadow boundary. A rule aimed at its container could
     never have matched, however correct the selector looked.

     This is a black box, so it is handled defensively rather than by betting on
     one structure. The floating node might be the host, a light-DOM wrapper, or
     something inside the shadow root — so all three are searched, and the test
     is the computed position, not a class name the app is free to rename.

     The previous attempt ran once at load and found nothing, because the teaser
     mounts later. A bootstrap observer now waits for it and disconnects once it
     has something to work with, so the expensive whole-document watch does not
     stay running. */
  try {
    var LIFT = 'calc(112px + env(safe-area-inset-bottom))';

    var teaserNodes = function () {
      var roots = [];
      Array.prototype.forEach.call(
        document.querySelectorAll('shopify-forms-embed, [id^="app-embed-container-"]'),
        function (host) {
          roots.push(host);
          if (host.shadowRoot) {
            Array.prototype.push.apply(
              roots,
              Array.prototype.slice.call(host.shadowRoot.querySelectorAll('*'))
            );
          } else {
            Array.prototype.push.apply(
              roots,
              Array.prototype.slice.call(host.querySelectorAll('*'))
            );
          }
        }
      );
      return roots.filter(function (node) {
        return window.getComputedStyle(node).position === 'fixed';
      });
    };

    var liftTeaser = function () {
      var up = document.body.classList.contains('epdp-bar-up') &&
               window.matchMedia('(max-width: 900px)').matches;
      var found = teaserNodes();
      found.forEach(function (node) {
        node.style.setProperty('transition', 'bottom .28s ease');
        if (up) node.style.setProperty('bottom', LIFT, 'important');
        else node.style.removeProperty('bottom');
      });
      return found.length;
    };

    // Watch the document only until the widget exists, then stop.
    var bootstrap = new MutationObserver(function () {
      if (liftTeaser() > 0) {
        bootstrap.disconnect();
        var host = document.querySelector('shopify-forms-embed');
        if (host && host.shadowRoot) {
          new MutationObserver(liftTeaser)
            .observe(host.shadowRoot, { childList: true, subtree: true });
        }
      }
    });
    bootstrap.observe(document.documentElement, { childList: true, subtree: true });

    // The bar toggling and a resize across the breakpoint both need a re-run.
    new MutationObserver(liftTeaser)
      .observe(document.body, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', liftTeaser);

    liftTeaser();

    // If this fires, the widget is built some other way again and the next
    // attempt should start from what the console says, not from a guess.
    setTimeout(function () {
      if (teaserNodes().length === 0) {
        console.warn('[epdp] newsletter teaser: no fixed-positioned node found under ' +
                     'shopify-forms-embed or [id^="app-embed-container-"]');
      }
    }, 4000);
  } catch (e) { console.error('[epdp] newsletter teaser offset failed:', e); }

  /* ── Quantity ─────────────────────────────────────────────────────────── */
  var qtyInput = root.querySelector('[data-epdp-qty]');
  function step(delta) {
    if (!qtyInput) return;
    qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + delta);
  }
  var minus = root.querySelector('[data-epdp-qty-minus]');
  var plus = root.querySelector('[data-epdp-qty-plus]');
  if (minus) minus.addEventListener('click', function () { step(-1); });
  if (plus) plus.addEventListener('click', function () { step(1); });

  /* ── Buy more, save more ────────────────────────────────────────────────
     Clicking a tier sets the quantity to that tier's number. The number is
     read from the label rather than rendered as a data attribute, because the
     label is free text a merchant types — "Buy 3", "3+", "Buy 3 or more" all
     have to work, and Liquid has no way to pull the first integer out of an
     arbitrary string. */
  try {
    all('[data-epdp-bmsm-tier]', root).forEach(function (tier) {
      var label = tier.querySelector('[data-epdp-bmsm-label]');
      var match = label && label.textContent.match(/\d+/);
      if (!match) return;
      var qty = parseInt(match[0], 10);
      if (!qty || qty < 1) return;

      tier.addEventListener('click', function () {
        if (!qtyInput) return;
        qtyInput.value = qty;
        // The ATC label and any listener watching quantity need to know.
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  } catch (e) { console.error('[epdp] buy more save more failed:', e); }

  /* ── Trust tiles ────────────────────────────────────────────────────────
     One dialog, filled from the clicked tile's <template>. Tiles are keyed by
     index rather than by title, so a title containing a quote cannot break the
     selector. */
  var trustDialog = document.querySelector('[data-epdp-trust-dialog]');
  if (trustDialog) {
    var tTitle = trustDialog.querySelector('[data-epdp-trust-dialog-title]');
    var tBody = trustDialog.querySelector('[data-epdp-trust-dialog-body]');
    var tClose = trustDialog.querySelector('[data-epdp-trust-dialog-close]');

    // Fetched page markup, kept per URL so reopening a tile costs nothing.
    var pageCache = {};

    // A policy page is built from a section, not the page's rich-text field, so
    // the copy only exists in the rendered page. Pull that section out — with
    // its own <style>, which is what makes it look right inside the dialog.
    function extractPageSection(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.querySelector('main .shopify-section')
              || doc.querySelector('[id^="shopify-section-template--"]');
      return node ? node.innerHTML : '';
    }

    function fillFromPage(url) {
      if (pageCache[url] !== undefined) {
        tBody.innerHTML = pageCache[url];
        return;
      }
      tBody.innerHTML = '<p class="epdp-trust-dialog__loading">Loading…</p>';
      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('page ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var markup = extractPageSection(html);
          if (!markup) throw new Error('no section found');
          pageCache[url] = markup;
          tBody.innerHTML = markup;
        })
        .catch(function () {
          // Never leave the shopper staring at a spinner: hand them the page.
          tBody.innerHTML = '<p class="epdp-trust-dialog__more"><a href="' + url + '">Read the full page &rarr;</a></p>';
        });
    }

    all('[data-epdp-trust-tile]').forEach(function (tile) {
      tile.addEventListener('click', function () {
        if (typeof trustDialog.showModal !== 'function') return;
        if (tTitle) tTitle.textContent = tile.dataset.epdpTrustTitle || '';

        var pageUrl = tile.dataset.epdpTrustPage;
        if (pageUrl && tBody) {
          fillFromPage(pageUrl);
          trustDialog.showModal();
          return;
        }

        var key = tile.dataset.epdpTrustTile;
        var tpl = document.querySelector('template[data-epdp-trust-detail="' + key + '"]');
        if (!tpl) return;
        if (tBody) {
          tBody.innerHTML = '';
          tBody.appendChild(tpl.content.cloneNode(true));
        }
        trustDialog.showModal();
      });
    });

    if (tClose) tClose.addEventListener('click', function () { trustDialog.close(); });
    trustDialog.addEventListener('click', function (e) {
      if (e.target === trustDialog) trustDialog.close();
    });
  }

  /* ── Size chart ─────────────────────────────────────────────────────────
     A <dialog> when a chart image is configured for this product type; the
     browser handles Escape, the focus trap and the inert background. With no
     chart set the link still works, scrolling to the A+ sheet, which carries
     the sizing too. */
  var sizeDialog = document.querySelector('[data-epdp-sizechart]');
  var canDialog = sizeDialog && typeof sizeDialog.showModal === 'function';

  all('[data-epdp-sizeguide-link]').forEach(function (link) {
    link.addEventListener('click', function () {
      if (canDialog) { sizeDialog.showModal(); return; }
      var target = document.getElementById('epdp-size-guide') || document.getElementById('epdp-aplus');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  if (sizeDialog) {
    var closeBtn = sizeDialog.querySelector('[data-epdp-sizechart-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { sizeDialog.close(); });

    // Click outside the picture closes it. The dialog box is the backdrop's
    // hit area, so a click landing on the element itself is a click outside.
    sizeDialog.addEventListener('click', function (e) {
      if (e.target === sizeDialog) sizeDialog.close();
    });
  }

  /* ── Delivery estimate ────────────────────────────────────────────────── */
  var delivery = root.querySelector('[data-epdp-delivery]');
  if (delivery) {
    var proc = parseInt(delivery.dataset.proc, 10);
    var ship = parseInt(delivery.dataset.ship, 10);
    if (isNaN(proc)) proc = 2;
    if (isNaN(ship)) ship = 5;

    var now = new Date();
    var addDays = function (d, n) { var r = new Date(d); r.setDate(r.getDate() + n); return r; };
    var fmt = function (d) { return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

    var shipFrom = addDays(now, proc);
    var shipTo = addDays(now, proc + 1);
    var delFrom = addDays(now, proc + ship);
    var delTo = addDays(now, proc + ship + 2);

    var set = function (sel, text) {
      var el = delivery.querySelector(sel);
      if (el) el.textContent = text;
    };
    set('[data-epdp-eta-ordered]', fmt(now));
    set('[data-epdp-eta-ships]', fmt(shipFrom) + '–' + fmt(shipTo));
    set('[data-epdp-eta-delivered]', fmt(delFrom) + '–' + fmt(delTo));
    set('[data-epdp-eta-range]', fmt(delFrom) + '–' + fmt(delTo));
  }

  /* ── Add to cart ──────────────────────────────────────────────────────── */
  function addToCart(button) {
    if (!current || !current.available || button.dataset.busy === '1') return;
    button.dataset.busy = '1';
    var label = button.querySelector('[data-epdp-atc-label]');
    var before = label ? label.textContent : '';
    if (label) label.textContent = 'ADDING…';

    fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: current.id,
        quantity: parseInt(qtyInput ? qtyInput.value : 1, 10) || 1
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Add to cart failed: ' + res.status);
        return fetch('/cart.js');
      })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (window.Alpine && window.Alpine.store && window.Alpine.store('cart')) {
          window.Alpine.store('cart').update(cart.item_count);
        }
        window.dispatchEvent(new CustomEvent('open-cart'));
        if (label) label.textContent = 'ADDED TO BAG';
        setTimeout(function () { if (label) label.textContent = before; }, 2000);
      })
      .catch(function (err) {
        console.error(err);
        if (label) label.textContent = before;
      })
      .finally(function () { button.dataset.busy = '0'; });
  }

  atcButtons.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      addToCart(btn);
    });
  });

  /* ── Shop now ───────────────────────────────────────────────────────────
     Adds the selected variant, then goes straight to checkout. Only used when
     the block is set to "Shop now"; the wallet setting hands off to Shopify's
     own accelerated checkout instead, which is the only path that gives Shop
     Pay one-tap with saved details. */
  all('[data-epdp-buynow]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (!current || !current.available || btn.dataset.busy === '1') return;
      btn.dataset.busy = '1';
      var label = btn.querySelector('[data-epdp-buynow-label]');
      var before = label ? label.textContent : '';
      if (label) label.textContent = 'ONE MOMENT…';

      fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: current.id,
          quantity: parseInt(qtyInput ? qtyInput.value : 1, 10) || 1
        })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed: ' + res.status);
          window.location.href = window.routes && window.routes.cart_url
            ? window.routes.cart_url.replace(/\/cart$/, '') + '/checkout'
            : '/checkout';
        })
        .catch(function (err) {
          // Leaving the shopper on a spinner with no cart would be worse than
          // showing the button again and letting them retry.
          console.error(err);
          if (label) label.textContent = before;
          btn.dataset.busy = '0';
        });
    });
  });

  /* ── Horizontal sliders (recommendations, collections) ────────────────── */
  all('[data-epdp-slider]').forEach(function (slider) {
    var scope = slider.closest('[data-epdp-slider-scope]') || document;
    var prev = scope.querySelector('[data-epdp-slider-prev]');
    var next = scope.querySelector('[data-epdp-slider-next]');

    function sync() {
      if (!prev || !next) return;
      var max = slider.scrollWidth - slider.clientWidth - 2;
      prev.disabled = slider.scrollLeft <= 2;
      next.disabled = slider.scrollLeft >= max;
    }
    function nudge(dir) {
      slider.scrollBy({ left: dir * slider.clientWidth * 0.75, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });
    slider.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  });

  /* ── Recommendations ────────────────────────────────────────────────────
     Fetched rather than rendered inline so the recommendation query never sits
     on the critical path for a paid-traffic landing page. */
  var recs = document.querySelector('[data-epdp-recommendations]');
  if (recs && recs.dataset.url) {
    fetch(recs.dataset.url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-epdp-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          recs.innerHTML = fresh.innerHTML;
          // The markup was replaced, so re-bind the arrows to the new node.
          var scope = recs.closest('[data-epdp-slider-scope]');
          if (scope) {
            var p = scope.querySelector('[data-epdp-slider-prev]');
            var n = scope.querySelector('[data-epdp-slider-next]');
            if (p) p.onclick = function () { recs.scrollBy({ left: -recs.clientWidth * 0.75, behavior: 'smooth' }); };
            if (n) n.onclick = function () { recs.scrollBy({ left: recs.clientWidth * 0.75, behavior: 'smooth' }); };
          }
        } else {
          var host = recs.closest('section');
          if (host) host.hidden = true;
        }
      })
      .catch(function () { /* a missing carousel is not worth an error state */ });
  }
})();
